import { handleErrorMessage } from "../validation/profileData.validation";

const DEFAULT_MESSAGE = "Something went wrong. Please try again.";

function coerceToString(value) {
  if (typeof value === "string") return value.trim();
  if (value == null) return "";
  try {
    // Avoid leaking structured internals; stringify only if it's small and plain.
    if (typeof value === "object") return "";
    return String(value).trim();
  } catch {
    return "";
  }
}

function getStatus(err) {
  const status = err?.response?.status ?? err?.status ?? err?.code;
  return typeof status === "number" ? status : undefined;
}

function looksDbLike(message) {
  return /(duplicate key value|violates unique constraint|unique constraint|insert into|update\s+\"|delete from|syntax error at or near|sqlstate|\b2350[035]\b)/i.test(
    message
  );
}

function looksLikeStackOrPath(message) {
  return /(\bat\b\s+\S+\s+\(|\/home\/|[A-Za-z]:\\|\.ts:\d+|\.js:\d+|stack)/i.test(
    message
  );
}

function containsLikelyPii(message) {
  // Very conservative: if the message contains an email-like token or a long phone-ish digit run,
  // don't show it verbatim.
  const hasEmail = /\b\S+@\S+\.\S+\b/.test(message);
  const hasLongPhoneLike = /\+?\d[\d\s().-]{9,}\d/.test(message);
  const hasJwtLike = /\beyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9._-]{10,}\.[a-zA-Z0-9._-]{10,}\b/.test(
    message
  );
  return hasEmail || hasLongPhoneLike || hasJwtLike;
}

function safeByStatus(status) {
  switch (status) {
    case 400:
      return "Invalid request. Please check your input.";
    case 401:
      return "Please sign in again.";
    case 403:
      return "You don’t have permission to do that.";
    case 404:
      return "Not found.";
    case 409:
      return "That value is already in use.";
    case 429:
      return "Too many requests. Please try again later.";
    default:
      if (typeof status === "number" && status >= 500) return DEFAULT_MESSAGE;
      return DEFAULT_MESSAGE;
  }
}

function getNestedResolverMessage(err) {
  const nested = err?.response?.data?.data;
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) return "";

  for (const value of Object.values(nested)) {
    if (!value || typeof value !== "object") continue;
    const msg = coerceToString(value.message);
    if (msg) return msg;
  }

  return "";
}

/**
 * Extracts a user-safe error message regardless of backend sanitization state.
 * Never returns DB internals/PII; always returns a non-empty string.
 */
export function getApiErrorMessage(err) {
  const status = getStatus(err);
  const topLevelMessage = coerceToString(err?.response?.data?.message);
  const nestedResolverMessage = getNestedResolverMessage(err);
  const shouldPreferNested =
    topLevelMessage.toLowerCase() === "error resolving data" && nestedResolverMessage;

  const candidate = coerceToString(
    (shouldPreferNested ? nestedResolverMessage : err?.response?.data?.message) ??
      err?.response?.data?.error ??
      err?.message ??
      ""
  );

  // If backend provided a safe message (new format), accept it unless it looks unsafe.
  const unsafe =
    !candidate ||
    looksDbLike(candidate) ||
    looksLikeStackOrPath(candidate) ||
    containsLikelyPii(candidate);

  if (!unsafe) return candidate;

  // Legacy unique constraint parsing (raw DB message) → safe string
  const legacy = handleErrorMessage(candidate);
  if (legacy) return legacy;

  // Network-ish Axios errors
  if (!status) {
    const net = coerceToString(err?.message);
    if (/network error|timeout|failed to fetch/i.test(net)) {
      return "Network error. Please check your connection and try again.";
    }
  }

  return safeByStatus(status);
}

